import { forwardRef, useCallback, useEffect, useMemo, useState, type ReactElement, type ReactNode, type Ref } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DangerousOutlinedIcon from "@mui/icons-material/DangerousOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import {
  parseMarkdownTable,
  selectMoreDiagramImages,
  sourceMediaKind,
  videoDisplayName,
  type SourceMediaKind,
} from "../../utils/ragChatUtils";
import type {
  RagAlert,
  RagAlternativeSource,
  RagImageItem,
  RagPrimarySource,
  RagSource,
  RagVideoItem,
} from "../../types/ragChatTypes";
import { ALERT_CARD_STYLES } from "../../constants/ragUiConstants";
import { flattenSortedPageAlerts, formatAlertSourceLine, stripEmbeddedAlertSource } from "../../utils/answerContentUtils";
import { ragPdfUrl, ragVideoPreviewUrl } from "../../services/ragChatApi";
import { RagImageLightbox } from "./RagImageLightbox";
import { RagMoreDiagramsCarousel } from "./RagMoreDiagramsCarousel";
import { VideoMetadataThumbnail } from "../../../document-management/components/shared/VideoMetadataThumbnail";
import { useTranslation } from "react-i18next";
import { RagMarkdownContent } from "./RagMarkdownContent";

const pdfBlobUrlCache = new Map<string, string>();
const pdfBlobInFlight = new Map<string, Promise<string>>();
const MAX_PDF_BLOB_CACHE = 16;
const SECTION_COUNT_PILL_SX = {
  ml: 1,
  px: 1,
  py: 0.25,
  borderRadius: 10,
  bgcolor: "grey.200",
  color: "text.secondary",
  fontSize: 12,
  fontWeight: 700,
  minWidth: 22,
  textAlign: "center" as const,
};

const SafetyNoticesSlideTransition = forwardRef(function SafetyNoticesSlideTransition(
  props: TransitionProps & { children: ReactElement },
  ref: Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MEDIA_CARD_SX = {
  borderRadius: 1.5,
  overflow: "hidden",
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  boxShadow: "0 1px 4px rgba(28,46,54,0.08)",
  transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(28,46,54,0.14)",
  },
  "&:focus-visible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: 2,
  },
};

function SectionToggleHeader({
  title,
  count,
  open,
  onToggle,
  icon,
  subtitle,
}: {
  title: string;
  count?: number;
  open: boolean;
  onToggle: () => void;
  icon?: ReactNode;
  subtitle?: string;
}) {
  return (
    <Button
      fullWidth
      size="small"
      onClick={onToggle}
      sx={{
        textTransform: "none",
        color: "text.secondary",
        px: 0,
        py: 0.25,
        minWidth: 0,
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, gap: 0.75 }}>
        {icon}
        <Typography variant="labelSmall" fontWeight={700} noWrap>
          {title}
        </Typography>
        {typeof count === "number" && (
          <Box component="span" sx={SECTION_COUNT_PILL_SX}>
            {count}
          </Box>
        )}
      </Box>
      {subtitle && (
        <Typography
          variant="bodySmall"
          color="text.secondary"
          sx={{
            ml: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {subtitle}
        </Typography>
      )}
      <ExpandMoreIcon
        sx={{ transform: open ? "rotate(0deg)" : "rotate(-90deg)", transition: "0.15s" }}
      />
    </Button>
  );
}

function cachePdfBlobUrl(pdfName: string, blobUrl: string) {
  const existing = pdfBlobUrlCache.get(pdfName);
  if (existing && existing !== blobUrl) {
    URL.revokeObjectURL(existing);
  }
  if (existing) {
    pdfBlobUrlCache.delete(pdfName);
  }
  pdfBlobUrlCache.set(pdfName, blobUrl);

  while (pdfBlobUrlCache.size > MAX_PDF_BLOB_CACHE) {
    const oldestKey = pdfBlobUrlCache.keys().next().value as string | undefined;
    if (!oldestKey) break;
    const oldestUrl = pdfBlobUrlCache.get(oldestKey);
    if (oldestUrl) {
      URL.revokeObjectURL(oldestUrl);
    }
    pdfBlobUrlCache.delete(oldestKey);
  }
}

async function ensurePdfBlobUrl(pdfName: string): Promise<string> {
  const cached = pdfBlobUrlCache.get(pdfName);
  if (cached) return cached;

  const existingReq = pdfBlobInFlight.get(pdfName);
  if (existingReq) return existingReq;

  const token = localStorage.getItem("jwtToken") || "";
  const req = (async () => {
    const res = await fetch(ragPdfUrl(pdfName), {
      headers: token ? { Authorization: token } : undefined,
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(txt || `Failed to load PDF (HTTP ${res.status})`);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    cachePdfBlobUrl(pdfName, url);
    pdfBlobInFlight.delete(pdfName);
    return url;
  })().catch((e) => {
    pdfBlobInFlight.delete(pdfName);
    throw e;
  });

  pdfBlobInFlight.set(pdfName, req);
  return req;
}

function TableCard({
  tableMarkdown,
  page,
  tableIndex,
}: {
  tableMarkdown: string;
  page?: string;
  tableIndex?: number;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const parsed = parseMarkdownTable(tableMarkdown);
  if (!parsed) return null;
  const { headers, rows } = parsed;

  return (
    <Box
      className="rag-table-card"
      sx={{
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Button
        fullWidth
        onClick={() => setCollapsed((c) => !c)}
        sx={{
          justifyContent: "space-between",
          py: 0.75,
          px: 1.5,
          bgcolor: "#f3f9fe",
          borderRadius: 0,
          textTransform: "none",
          borderBottom: collapsed ? 0 : "1px solid",
          borderColor: "primary.light",
        }}
        endIcon={
          <ExpandMoreIcon
            sx={{
              transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
              transition: "transform 0.15s",
            }}
          />
        }
      >
        <Typography variant="labelSmall" fontWeight={700} color="primary.dark">
          {tableIndex !== undefined ? `Table ${tableIndex + 1}` : "Table"}
          {page && (
            <Typography component="span" variant="labelSmall" color="text.secondary" sx={{ ml: 1 }}>
              — p.{page}
            </Typography>
          )}
        </Typography>
      </Button>
      <Collapse in={!collapsed}>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 360 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.800" }}>
                {headers.map((h, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      color: "common.white",
                      fontWeight: 600,
                      borderColor: "divider",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h || "—"}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, ri) => (
                <TableRow
                  key={ri}
                  sx={{
                    bgcolor: ri % 2 === 0 ? "background.paper" : "grey.50",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  {row.map((cell, ci) => (
                    <TableCell
                      key={ci}
                      sx={{
                        borderColor: "divider",
                        fontWeight: ci === 0 ? 600 : 400,
                      }}
                    >
                      {cell || "—"}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Collapse>
    </Box>
  );
}

function CollapsibleTablesGroup({
  tables,
}: {
  tables: { page: string; md: string; idx: number }[];
}) {
  const [expanded, setExpanded] = useState(false);
  const first = tables[0];
  if (!first) return null;
  if (tables.length === 1) {
    return (
      <TableCard tableMarkdown={first.md} page={first.page} tableIndex={first.idx} />
    );
  }
  const extra = tables.length - 1;
  return (
    <Stack spacing={1}>
      <Box sx={{ position: "relative" }}>
        <TableCard tableMarkdown={first.md} page={first.page} tableIndex={first.idx} />
        <Button
          size="small"
          variant="contained"
          color="inherit"
          onClick={() => setExpanded((v) => !v)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            minWidth: 0,
            px: 1,
            py: 0.25,
            fontSize: 12,
            fontWeight: 700,
            boxShadow: 2,
            bgcolor: "background.paper",
            "&:hover": { bgcolor: "grey.100" },
          }}
          endIcon={
            <ExpandMoreIcon
              fontSize="small"
              sx={{ transform: expanded ? "rotate(180deg)" : "none", transition: "0.15s" }}
            />
          }
          aria-label={expanded ? "Collapse tables" : "Expand tables"}
        >
          {expanded ? "Hide" : `+${extra}`}
        </Button>
      </Box>
      <Collapse in={expanded}>
        <Stack spacing={1}>
          {tables.slice(1).map(({ page, md, idx }) => (
            <TableCard key={`${page}-${idx}`} tableMarkdown={md} page={page} tableIndex={idx} />
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
}

export function RagTablesSection({
  pageTables,
  defaultOpen = true,
}: {
  pageTables: Record<string, string[]>;
  defaultOpen?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const allTables = useMemo(() => {
    const pages = Object.keys(pageTables).sort(
      (a, b) => (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0)
    );
    return pages.flatMap((page) =>
      (pageTables[page] || []).map((md, idx) => ({ page, md, idx }))
    );
  }, [pageTables]);

  if (!allTables.length) return null;

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
      <SectionToggleHeader
        title={t("opva.chatAssistanceSpecificationTables")}
        count={allTables.length}
        open={open}
        onToggle={() => setOpen((o) => !o)}
      />
      <Collapse in={open}>
        <CollapsibleTablesGroup tables={allTables} />
      </Collapse>
    </Box>
  );
}

function AlertLevelIcon({ level, size = 22 }: { level: string; size?: number }) {
  const s = ALERT_CARD_STYLES[level] ?? ALERT_CARD_STYLES.note;
  const iconSx = { color: s.labelColor, fontSize: size, flexShrink: 0 };
  switch (level) {
    case "danger":
      return <DangerousOutlinedIcon sx={iconSx} />;
    case "warning":
      return <WarningAmberOutlinedIcon sx={iconSx} />;
    case "caution":
      return <ReportProblemOutlinedIcon sx={iconSx} />;
    default:
      return <InfoOutlinedIcon sx={iconSx} />;
  }
}

function RagAlertCard({
  level,
  text,
  page,
  sourceDocumentName,
  pageLabelShort = "p.",
}: {
  level: string;
  text: string;
  page?: string;
  sourceDocumentName?: string;
  pageLabelShort?: string;
}) {
  const s = ALERT_CARD_STYLES[level] ?? ALERT_CARD_STYLES.note;
  const sourceLine = formatAlertSourceLine(page, sourceDocumentName, pageLabelShort);
  const noticeText = stripEmbeddedAlertSource(text);
  const hasStructuredSource = sourceLine !== "From source";

  return (
    <Box
      sx={{
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: s.bg,
        borderLeft: `4px solid ${s.accent}`,
        boxShadow: "0 1px 2px rgba(28,46,54,0.05)",
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        minWidth: 0,
      }}
    >
      <Box
        className="notice-type"
        sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}
      >
        <AlertLevelIcon level={level} size={16} />
        <Typography
          variant="labelNormal"
          sx={{
            fontWeight: 600,
            letterSpacing: 0.4,
            color: s.labelColor,
            lineHeight: 1.3,
          }}
        >
          {s.label}
        </Typography>
      </Box>

      <Box
        className="notice-content"
        component="div"
        sx={{
          display: "block",
          typography: "bodySmall",
          color: s.textColor,
          lineHeight: 1.5,
          mb: 1,
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
        }}
      >
        {noticeText}
      </Box>

      <Box
        className="notice-source"
        component="div"
        sx={{
          display: "block",
          typography: "labelSmall",
          color: "text.secondary",
          lineHeight: 1.4,
        }}
      >
        {hasStructuredSource ? `Source: ${sourceLine}` : "From source"}
      </Box>
    </Box>
  );
}

function SafetyNoticesDialog({
  open,
  onClose,
  title,
  count,
  alerts,
  sourceDocumentName,
  pageLabelShort = "p.",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  count: number;
  alerts: ReturnType<typeof flattenSortedPageAlerts>;
  sourceDocumentName?: string;
  pageLabelShort?: string;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      scroll="paper"
      TransitionComponent={SafetyNoticesSlideTransition}
      sx={{
        "& .MuiDialog-container": {
          alignItems: "flex-end",
          justifyContent: "center",
        },
      }}
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: { xs: "100%", sm: 720, md: 760 },
          m: 0,
          borderRadius: "16px 16px 0 0",
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: "0 -4px 24px rgba(28,46,54,0.14)",
          maxHeight: isMobile ? "70vh" : "min(70vh, 520px)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexShrink: 0,
          minHeight: 56,
          py: 1.25,
          px: { xs: 1.5, sm: 2 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, gap: 0.75 }}>
          <ShieldOutlinedIcon sx={{ color: "primary.main", fontSize: 20, flexShrink: 0 }} />
          <Typography variant="labelSmall" fontWeight={700} noWrap>
            {title}
          </Typography>
          <Box component="span" sx={{ ...SECTION_COUNT_PILL_SX, ml: 0 }}>
            {count}
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close safety notices"
          size="small"
          sx={{ color: "text.secondary", ml: 0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          overflowY: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Stack spacing={1.5}>
          {alerts.map((a) => (
            <RagAlertCard
              key={a.key}
              level={a.level}
              text={a.text}
              page={a.page}
              sourceDocumentName={sourceDocumentName}
              pageLabelShort={pageLabelShort}
            />
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export function RagAlertsSection({
  pageAlerts,
  placement = "bottom",
  sourceDocumentName,
  pageLabelShort = "p.",
}: {
  pageAlerts: Record<string, RagAlert[]>;
  /** When "top", renders above the answer (no top border). */
  placement?: "top" | "bottom";
  sourceDocumentName?: string;
  pageLabelShort?: string;
}) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const sortedAlerts = useMemo(() => flattenSortedPageAlerts(pageAlerts), [pageAlerts]);

  if (!sortedAlerts.length) return null;

  const isTop = placement === "top";
  const title = t("opva.chatAssistanceSafetyNotices");

  return (
    <>
      <Box
        sx={{
          ...(isTop ? { mb: 2 } : { mt: 2 }),
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(28,46,54,0.05)",
        }}
      >
        <Box sx={{ px: 1.5, py: 1, bgcolor: "grey.50" }}>
          <Button
            fullWidth
            size="small"
            onClick={() => setModalOpen(true)}
            sx={{
              textTransform: "none",
              color: "text.secondary",
              px: 0,
              py: 0.25,
              minWidth: 0,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, gap: 0.75 }}>
              <ShieldOutlinedIcon sx={{ color: "primary.main", fontSize: 18 }} />
              <Typography variant="labelSmall" fontWeight={700} noWrap>
                {title}
              </Typography>
              <Box component="span" sx={SECTION_COUNT_PILL_SX}>
                {sortedAlerts.length}
              </Box>
            </Box>
            <ChevronRightIcon sx={{ fontSize: 20, color: "text.disabled", flexShrink: 0 }} />
          </Button>
        </Box>
      </Box>

      <SafetyNoticesDialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={title}
        count={sortedAlerts.length}
        alerts={sortedAlerts}
        sourceDocumentName={sourceDocumentName}
        pageLabelShort={pageLabelShort}
      />
    </>
  );
}

export function RagOptionButtons({
  options,
  selectedOption,
  disabled,
  onSelect,
}: {
  options: string[];
  selectedOption: string | null;
  disabled?: boolean;
  onSelect: (opt: string) => void;
}) {
  if (!options?.length) return null;
  return (
    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
      {options.map((opt, i) => {
        const selected = selectedOption === opt;
        const lock = disabled || !!selectedOption;
        return (
          <Button
            key={i}
            variant={selected ? "contained" : "outlined"}
            color="primary"
            size="small"
            disabled={lock}
            onClick={() => onSelect(opt)}
            sx={{ borderRadius: 999, textTransform: "none" }}
          >
            {selected ? "✓ " : ""}
            {opt}
          </Button>
        );
      })}
    </Stack>
  );
}

export function RagRelatedQuestions({
  questions,
  onSelect,
}: {
  questions: string[];
  onSelect: (q: string) => void;
  maxVisible?: number;
}) {
  const { t } = useTranslation();
  if (!questions?.length) return null;
  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
      <Typography variant="labelSmall" color="text.secondary" fontWeight={700} gutterBottom>
        {t("opva.chatAssistanceRelatedQuestions")}
      </Typography>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {questions.map((q, i) => (
          <Button
            key={i}
            fullWidth
            variant="outlined"
            onClick={() => onSelect(q)}
            startIcon={<HelpOutlineIcon color="primary" />}
            sx={{
              justifyContent: "flex-start",
              textAlign: "left",
              textTransform: "none",
              py: 1.25,
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Typography variant="bodyNormal">{q}</Typography>
          </Button>
        ))}
      </Stack>
    </Box>
  );
}

export function RagAlternativePdfs({
  pdfs,
  onSelect,
  defaultOpen = false,
}: {
  pdfs: string[];
  onSelect: (pdf: string) => void;
  defaultOpen?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  if (!pdfs?.length) return null;
  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
      <SectionToggleHeader
        title={t("opva.chatAssistanceAlternativePdfs")}
        count={pdfs.length}
        open={open}
        onToggle={() => setOpen((o) => !o)}
      />
      <Collapse in={open}>
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
          {pdfs.map((pdf, i) => {
            const label = pdf.replace(/\.pdf$/i, "").replace(/_/g, " ");
            return (
              <Button
                key={i}
                size="small"
                variant="outlined"
                onClick={() => onSelect(pdf)}
                sx={{ borderRadius: 999, textTransform: "none" }}
              >
                {label}
              </Button>
            );
          })}
        </Stack>
      </Collapse>
    </Box>
  );
}

// ── DF-1 / DF-2 — Ranked Source Presentation ─────────────────────────────────

function prettyPdfLabel(pdf: string): string {
  return pdf.replace(/\.pdf$/i, "").replace(/_/g, " ");
}

function formatPages(pages?: number[]): string | null {
  if (!pages?.length) return null;
  const valid = pages.filter((p) => Number(p) > 0);
  if (!valid.length) return null;
  return valid.length === 1 ? `p.${valid[0]}` : `pp. ${valid.join(", ")}`;
}

type ChipColor = "default" | "success" | "warning" | "info";

function bandColor(band?: string): ChipColor {
  switch ((band ?? "").toLowerCase()) {
    case "high":
      return "success";
    case "medium":
      return "warning";
    case "low":
      return "default";
    default:
      return "info";
  }
}

function SourceFileIcon({ kind }: { kind: SourceMediaKind }) {
  if (kind === "video") {
    return <PlayCircleOutlineIcon color="secondary" fontSize="small" />;
  }
  return <PictureAsPdfIcon color="error" fontSize="small" />;
}

/** "Best Answer" attribution header shown above the answer when a primary source exists. */
export function RagBestAnswerHeader({ primary }: { primary: RagPrimarySource }) {
  const pdf = primary.pdf_name || primary.source_label;
  if (!pdf) return null;
  const band = primary.relevance_band;
  const category = primary.document_category;
  const kind = sourceMediaKind(pdf);

  return (
    <Box
      sx={{
        mb: 1.5,
        p: 1.25,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        borderLeft: "4px solid",
        borderLeftColor: "primary.main",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, flexWrap: "wrap" }}>
        <Typography variant="labelSmall" fontWeight={700} color="primary.dark" sx={{ letterSpacing: 0.4 }}>
          BEST ANSWER
        </Typography>
        {band && <Chip size="small" color={bandColor(band)} label={band} sx={{ height: 20, fontSize: 11 }} />}
        {category && (
          <Chip size="small" variant="outlined" label={category} sx={{ height: 20, fontSize: 11 }} />
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        <SourceFileIcon kind={kind} />
        <Typography variant="bodySmall" fontWeight={600} noWrap title={pdf} sx={{ minWidth: 0 }}>
          {pdf}
        </Typography>
      </Box>
    </Box>
  );
}

function AlternativeSourceCard({
  source,
  onShowDetails,
}: {
  source: RagAlternativeSource;
  onShowDetails: (targetPdf: string) => void;
}) {
  const { t } = useTranslation();
  const pdf = source.pdf_name || source.source_label || "";
  if (!pdf) return null;
  const pages = formatPages(source.pages);
  const band = source.relevance_band;
  const category = source.document_category;
  const summary = source.summary || source.document_content || "";
  const targetPdf = source.show_details?.target_pdf || pdf;
  const kind = sourceMediaKind(pdf);

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: "0 1px 3px rgba(28,46,54,0.06)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        <SourceFileIcon kind={kind} />
        <Typography variant="bodySmall" fontWeight={600} noWrap title={pdf} sx={{ flex: 1, minWidth: 0 }}>
          {prettyPdfLabel(pdf)}
        </Typography>
      </Box>

      <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.75 }}>
        {band && <Chip size="small" color={bandColor(band)} label={band} sx={{ height: 20, fontSize: 11 }} />}
        {category && (
          <Chip size="small" variant="outlined" label={category} sx={{ height: 20, fontSize: 11 }} />
        )}
        {pages && kind === "pdf" && (
          <Chip
            size="small"
            variant="outlined"
            label={pages}
            sx={{ height: 20, fontSize: 11 }}
          />
        )}
      </Stack>

      {summary && (
        <Typography variant="bodySmall" color="text.secondary" sx={{ mt: 1, lineHeight: 1.5 }}>
          {summary}
        </Typography>
      )}

    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<ArticleOutlinedIcon sx={{ fontSize: 14 }} />}
      onClick={() => onShowDetails(targetPdf)}
      sx={{
        ml: 1,
        mt: 0.5,                // Less top spacing
        minHeight: 28,
        py: 0.25,
        px: 1.25,
        fontSize: "0.75rem",
        lineHeight: 1.2,
        borderRadius: "16px",
        textTransform: "none",
        fontWeight: 500,
        verticalAlign: "middle",
        transform: "translateY(-3px)", // Move slightly upward

        "& .MuiButton-startIcon": {
          mr: 0.5,
          ml: -0.2,
        },

        "&:hover": {
          backgroundColor: "action.hover",
        },
      }}
    >
      {t("opva.chatAssistanceShowDetails")}
    </Button>
    </Box>
  );
}

export function RagAlternativeSources({
  sources,
  onShowDetails,
  defaultOpen = true,
}: {
  sources: RagAlternativeSource[];
  onShowDetails: (targetPdf: string) => void;
  defaultOpen?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  if (!sources?.length) return null;

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
      <SectionToggleHeader
        title={t("opva.chatAssistanceAlternativeSources")}
        count={sources.length}
        open={open}
        onToggle={() => setOpen((o) => !o)}
        icon={<ArticleOutlinedIcon sx={{ color: "text.secondary", fontSize: 18 }} />}
      />
      <Collapse in={open}>
        <Stack spacing={1.25} sx={{ mt: 1 }}>
          {sources.map((src, i) => (
            <AlternativeSourceCard
              key={`${src.pdf_name}-${i}`}
              source={src}
              onShowDetails={onShowDetails}
            />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

function PdfPreviewDialog({
  pdfName,
  initialPage,
  open,
  onClose,
}: {
  pdfName: string | null;
  initialPage?: number;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const page = initialPage && initialPage > 0 ? initialPage : 1;
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !pdfName) {
      setLoadError(null);
      setLoading(false);
      setBlobUrl(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setLoadError(null);
        setLoading(true);
        setBlobUrl(null);
        const url = await ensurePdfBlobUrl(pdfName);
        if (cancelled) {
          return;
        }
        setBlobUrl(url);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : "Failed to load PDF.");
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally omit blobUrl from deps; we revoke/replace within effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pdfName]);

  const iframeSrc = blobUrl
    ? `${blobUrl}${page > 1 ? `#page=${page}` : ""}`
    : "";
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle
        sx={{
          bgcolor: "grey.800",
          color: "common.white",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          {pdfName}
        </Box>
        {page > 1 && (
          <Chip size="small" label={`p.${page}`} sx={{ bgcolor: "primary.main", color: "common.white" }} />
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 360, p: 0 }}>
        {loading ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="bodyNormal" color="text.secondary">
              {t("opva.chatAssistanceLoadingDocument")}
            </Typography>
          </Box>
        ) : loadError ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="bodyNormal" color="error" sx={{ mb: 1 }}>
              {t("opva.chatAssistancePdfOpenError")}
            </Typography>
            <Typography variant="bodySmall" color="text.secondary">
              {loadError}
            </Typography>
          </Box>
        ) : blobUrl ? (
          <Box
            component="iframe"
            title={pdfName ?? "PDF preview"}
            src={iframeSrc || undefined}
            sx={{ width: "100%", height: 480, border: 0, display: "block" }}
          />
        ) : (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="bodyNormal" color="text.secondary">
              {t("opva.chatAssistanceNoDocumentSelected")}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export function RagSourcesList({
  sources,
  msgId,
  defaultOpen = false,
}: {
  sources: RagSource[];
  msgId: string;
  defaultOpen?: boolean;
}) {
  const { t } = useTranslation();
  const [openPdf, setOpenPdf] = useState<{ name: string; page: number } | null>(null);
  const [openVideo, setOpenVideo] = useState<{ title: string; src: string } | null>(null);
  const [open, setOpen] = useState(defaultOpen);
  const [showMorePages, setShowMorePages] = useState(false);
  const prefetchPdf = useCallback((pdfName: string) => {
    void ensurePdfBlobUrl(pdfName).catch(() => undefined);
  }, []);
  const sourceGroups = useMemo(() => {
    const map = new Map<string, { pages: number[]; kind: SourceMediaKind }>();
    for (const s of sources ?? []) {
      if (!s.pdf_name) continue;
      const page = parseInt(String(s.page ?? ""), 10);
      const pg = Number.isNaN(page) || page <= 0 ? 1 : page;
      const kind = sourceMediaKind(s.pdf_name, s.type);
      const existing = map.get(s.pdf_name);
      if (!existing) {
        map.set(s.pdf_name, { pages: [pg], kind });
        continue;
      }
      if (!existing.pages.includes(pg)) existing.pages.push(pg);
      if (kind === "video") existing.kind = "video";
    }

    const groups = Array.from(map.entries())
      .map(([pdf_name, { pages, kind }]) => {
        pages.sort((a, b) => a - b);
        return { pdf_name, pages, kind };
      })
      .sort((a, b) => a.pdf_name.localeCompare(b.pdf_name));

    const primary = groups.map((g) => ({
      pdf_name: g.pdf_name,
      page: g.pages[0] ?? 1,
      kind: g.kind,
    }));
    const extra = groups.flatMap((g) =>
      g.kind === "pdf"
        ? g.pages.slice(1).map((page) => ({ pdf_name: g.pdf_name, page, kind: g.kind }))
        : []
    );

    return { primary, extra };
  }, [sources]);

  const openSource = useCallback((pdf_name: string, page: number, kind: SourceMediaKind) => {
    if (kind === "video") {
      setOpenVideo({
        title: videoDisplayName(pdf_name),
        src: ragVideoPreviewUrl(pdf_name),
      });
      return;
    }
    setOpenPdf({ name: pdf_name, page });
  }, []);

  if (!sourceGroups.primary.length) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <SectionToggleHeader
        title={t("opva.chatAssistanceSources")}
        count={sourceGroups.primary.length}
        open={open}
        onToggle={() => setOpen((o) => !o)}
      />
      <Collapse in={open}>
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
        {sourceGroups.primary.map(({ pdf_name, page, kind }) => (
          <Button
            key={`${msgId}-${pdf_name}`}
            size="small"
            variant="outlined"
            startIcon={<SourceFileIcon kind={kind} />}
            onMouseEnter={() => {
              if (kind === "pdf") prefetchPdf(pdf_name);
            }}
            onFocus={() => {
              if (kind === "pdf") prefetchPdf(pdf_name);
            }}
            onClick={() => openSource(pdf_name, page, kind)}
            sx={{ borderRadius: 999, textTransform: "none" }}
          >
            {pdf_name}
            {kind === "pdf" && page > 1 && (
              <Typography component="span" variant="labelSmall" color="text.secondary" sx={{ ml: 0.75 }}>
                p.{page}
              </Typography>
            )}
          </Button>
        ))}
        {sourceGroups.extra.length > 0 && (
          <Button
            size="small"
            variant="text"
            onClick={() => setShowMorePages((v) => !v)}
            sx={{ textTransform: "none" }}
          >
            {showMorePages
              ? t("opva.chatAssistanceHideExtraPages")
              : t("opva.chatAssistanceMorePages", { count: sourceGroups.extra.length })}
          </Button>
        )}
      </Stack>
      <Collapse in={showMorePages} timeout="auto" unmountOnExit>
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
          {sourceGroups.extra.map(({ pdf_name, page, kind }) => (
            <Button
              key={`${msgId}-${pdf_name}-${page}`}
              size="small"
              variant="outlined"
              startIcon={<SourceFileIcon kind={kind} />}
              onMouseEnter={() => prefetchPdf(pdf_name)}
              onFocus={() => prefetchPdf(pdf_name)}
              onClick={() => openSource(pdf_name, page, kind)}
              sx={{ borderRadius: 999, textTransform: "none" }}
            >
              {pdf_name}
              <Typography component="span" variant="labelSmall" color="text.secondary" sx={{ ml: 0.75 }}>
                p.{page}
              </Typography>
            </Button>
          ))}
        </Stack>
      </Collapse>
      </Collapse>
      <PdfPreviewDialog
        pdfName={openPdf?.name ?? null}
        initialPage={openPdf?.page}
        open={!!openPdf}
        onClose={() => setOpenPdf(null)}
      />
      <Dialog open={!!openVideo} onClose={() => setOpenVideo(null)} maxWidth="md" fullWidth>
        <DialogTitle>{openVideo?.title}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {openVideo?.src ? (
            <Box
              component="video"
              controls
              src={openVideo.src}
              sx={{ width: "100%", maxHeight: 420, display: "block", bgcolor: "common.black" }}
            />
          ) : (
            <Typography variant="bodySmall" color="text.secondary" sx={{ p: 2 }}>
              {t("opva.chatAssistanceVideoUnavailable")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVideo(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export function RagVideosList({
  videos,
  msgId,
}: {
  videos: RagVideoItem[];
  msgId: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<{ title: string; src: string } | null>(null);
  if (!videos?.length) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography variant="labelSmall" color="text.secondary" fontWeight={700}>
          {t("opva.chatAssistanceInstructionalVideos")}
        </Typography>
        <Box component="span" sx={SECTION_COUNT_PILL_SX}>
          {videos.length}
        </Box>
      </Box>
      <Stack direction="row" flexWrap="wrap" gap={1.25}>
        {videos.map((item, idx) => {
          const label = item.name || videoDisplayName(item.path, `Video ${idx + 1}`);
          return (
            <Box
              component="button"
              type="button"
              key={`${msgId}-v-${idx}`}
              onClick={() =>
                setOpen({
                  title: label,
                  src: item.path ?? "",
                })
              }
              sx={{
                ...MEDIA_CARD_SX,
                width: { xs: "100%", sm: 248 },
                p: 0,
                textAlign: "left",
                cursor: "pointer",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  aspectRatio: "16 / 9",
                  bgcolor: "#eef3f8",
                  position: "relative",
                  overflow: "hidden",
                  "& video": {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    pointerEvents: "none",
                  },
                }}
              >
                {item.path ? (
                  <VideoMetadataThumbnail src={item.path} fallback={null} />
                ) : null}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "secondary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
                    }}
                  >
                    <PlayArrowIcon sx={{ color: "common.white", fontSize: 22, ml: "2px" }} />
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  px: 1.25,
                  py: 1,
                  minHeight: 48,
                  display: "flex",
                  alignItems: "flex-start",
                }}
              >
                <Typography
                  variant="bodySmall"
                  title={label}
                  sx={{
                    color: "text.primary",
                    fontWeight: 600,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.35,
                    minHeight: "2.7em",
                  }}
                >
                  {label}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>
      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="md" fullWidth>
        <DialogTitle>{open?.title}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {open?.src ? (
            <Box
              component="video"
              controls
              src={open.src}
              sx={{ width: "100%", maxHeight: 420, display: "block", bgcolor: "common.black" }}
            />
          ) : (
            <Typography variant="bodySmall" color="text.secondary" sx={{ p: 2 }}>
              {t("opva.chatAssistanceVideoUnavailable")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export function RagImageRail({
  images,
  inlineImages,
  msgId,
  answerText,
}: {
  images: RagImageItem[];
  inlineImages: Record<string, RagImageItem[]>;
  msgId: string;
  answerText: string;
}) {
  const uncited = selectMoreDiagramImages(images, inlineImages, answerText);
  return (
    <RagMoreDiagramsCarousel images={uncited} msgId={msgId} bordered showHeader />
  );
}
